import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiGet } from "../../api";
import { toast } from "react-toastify";
import SortableTableHeader from "../../components/SortableTableHeader";
import Pagination from "../../components/Pagination";

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
const labelCls =
  "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

const UserSalesReport = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [selectedUsername, setSelectedUsername] = useState("");

  const [filters, setFilters] = useState({ username: "", startDate: "", endDate: "" });
  const [tempFilters, setTempFilters] = useState({ username: "", startDate: "", endDate: "" });

  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const [sortedUsers, setSortedUsers] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "0.00";
    const numValue = Math.round(parseFloat(String(value)) * 100) / 100;
    return numValue.toFixed(2);
  };

  const sanitizeReportData = (data) => {
    if (!data) return data;
    const sanitized = { ...data };
    if (sanitized.totalSale) sanitized.totalSale = Math.round(parseFloat(sanitized.totalSale) * 100) / 100;
    if (sanitized.direct) Object.keys(sanitized.direct).forEach((key) => { if (typeof sanitized.direct[key] === "number") sanitized.direct[key] = Math.round(sanitized.direct[key] * 100) / 100; });
    if (sanitized.inDirect) Object.keys(sanitized.inDirect).forEach((key) => { if (typeof sanitized.inDirect[key] === "number") sanitized.inDirect[key] = Math.round(sanitized.inDirect[key] * 100) / 100; });
    if (sanitized.groups) {
      sanitized.groups = sanitized.groups.map((group) => {
        const g = { ...group };
        if (g.totalSales) g.totalSales = Math.round(parseFloat(g.totalSales) * 100) / 100;
        if (g.directSales) Object.keys(g.directSales).forEach((key) => { if (typeof g.directSales[key] === "number") g.directSales[key] = Math.round(g.directSales[key] * 100) / 100; });
        if (g.inDirectSales) Object.keys(g.inDirectSales).forEach((key) => { if (typeof g.inDirectSales[key] === "number") g.inDirectSales[key] = Math.round(parseFloat(g.inDirectSales[key]) * 100) / 100; });
        return g;
      });
    }
    return sanitized;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await apiGet("/api/admin/user/list?page=1&pageSize=1000&order=desc");
        if (res.data.success) {
          setUsers(res.data.data.data.filter((user) => user.role === "NU"));
        }
      } catch (err) {
        toast.error("Failed to fetch users");
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!filters.username) return users;
    const search = filters.username.toLowerCase();
    return users.filter((user) => {
      const username = user.username?.toLowerCase() || "";
      const name = user.name?.toLowerCase() || "";
      const surname = user.surname?.toLowerCase() || "";
      return username.includes(search) || name.includes(search) || surname.includes(search);
    });
  }, [users, filters.username]);

  const sortUsers = useCallback((usersToSort, sortConfig) => {
    if (!usersToSort.length || !sortConfig.key) return usersToSort;
    return [...usersToSort].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";
      if (sortConfig.key === "createdAt" || sortConfig.key === "updatedAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sortConfig.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
  }, []);

  useEffect(() => {
    setSortedUsers(sortUsers(filteredUsers, sort));
  }, [filteredUsers, sort, sortUsers]);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const generateReport = async (username, type) => {
    setSelectedUsername(username);
    setReportType(type);
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({ username, order: "desc" });
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);
      const endpoint = type === "sales" ? "/api/admin/reports/user-sales-report" : "/api/admin/reports/user-box-sales-report";
      const res = await apiGet(`${endpoint}?${queryParams}`);
      if (res.data.success) {
        const sanitizedData = sanitizeReportData(res.data.data);
        if (sanitizedData.groups && sanitizedData.groups.length > 0) {
          setReportData(sanitizedData);
          toast.success("Report generated successfully");
        } else {
          setReportData(null);
          toast.info(`No ${type === "sales" ? "sales" : "box sales"} data found for this user${filters.startDate || filters.endDate ? " in the selected date range" : ""}`);
        }
      } else {
        toast.error(res.data.message || "Failed to generate report");
      }
    } catch (err) {
      toast.error("Error generating report");
    } finally {
      setLoading(false);
    }
  };

  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setFilters({ ...tempFilters });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    const d = { username: "", startDate: "", endDate: "" };
    setTempFilters(d);
    setFilters(d);
    setReportData(null);
    setReportType(null);
    setSelectedUsername("");
    setCurrentPage(1);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Reports
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        User Sales <span className="text-gradient-ginox">Report</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">Generate detailed sales and box sales reports for NU users</p>

      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Search Username / Name</label>
            <input type="text" name="username" value={tempFilters.username} onChange={handleTempFilterChange} placeholder="Search by username or name" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Start Date</label>
            <input type="date" name="startDate" value={tempFilters.startDate} onChange={handleTempFilterChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input type="date" name="endDate" value={tempFilters.endDate} onChange={handleTempFilterChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} className={inputCls}>
              {[10, 15, 20, 25, 30, 50].map((s) => <option key={s} value={s}>{s} per page</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button type="button" onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="mb-6">
        <h2 className="text-base font-medium text-foreground mb-4">NU Users List</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <SortableTableHeader sortKey="sno" currentSort={sort} onSort={handleSort} className="text-left">S.No</SortableTableHeader>
                <SortableTableHeader sortKey="name" currentSort={sort} onSort={handleSort} className="text-left">Name</SortableTableHeader>
                <SortableTableHeader sortKey="username" currentSort={sort} onSort={handleSort} className="text-left">Username</SortableTableHeader>
                <SortableTableHeader sortKey="email" currentSort={sort} onSort={handleSort} className="text-left">Email</SortableTableHeader>
                <SortableTableHeader sortKey="accountStatus" currentSort={sort} onSort={handleSort} className="text-left">Status</SortableTableHeader>
                <SortableTableHeader sortable={false} className="text-left">Actions</SortableTableHeader>
              </tr>
            </thead>
            <tbody>
              {(sortedUsers.length > 0 ? sortedUsers : filteredUsers)
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((user, index) => (
                  <tr key={user._id} className="group hover:bg-white/[0.02] transition-colors">
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{(currentPage - 1) * pageSize + index + 1}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{user.name} {user.surname}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{user.username}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{user.email}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${user.accountStatus === "active" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-red-500/30 bg-red-500/10 text-red-400"}`}>
                        {user.accountStatus}
                      </span>
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      <div className="flex gap-2">
                        <button
                          onClick={() => generateReport(user.username, "sales")}
                          disabled={loading}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        >
                          {loading && selectedUsername === user.username && reportType === "sales" ? "Loading..." : "Sales Report"}
                        </button>
                        <button
                          onClick={() => generateReport(user.username, "box")}
                          disabled={loading}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        >
                          {loading && selectedUsername === user.username && reportType === "box" ? "Loading..." : "Box Report"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredUsers.length / pageSize)}
            totalRows={filteredUsers.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {/* Report Data */}
      {reportData && reportData.groups && reportData.groups.length > 0 ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Total Groups", value: reportData.totalGroups || 0, isCurrency: false },
              { label: "Total Sales", value: `$${formatCurrency(reportData.totalSale)}`, isCurrency: true },
              { label: "Direct Sales Count", value: reportData.direct?.total || 0, isCurrency: false },
              { label: "Indirect Sales", value: `$${formatCurrency(reportData.inDirect?.total)}`, isCurrency: true },
            ].map((card) => (
              <div key={card.label} className="rounded-lg border border-border/70 bg-card p-5">
                <p className={labelCls}>{card.label}</p>
                <p className="text-2xl font-bold text-foreground font-mono">{card.value}</p>
              </div>
            ))}
          </div>

          {reportType === "sales" && reportData.direct && reportData.inDirect && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-white/[0.08] rounded-lg p-5">
                <h3 className="text-sm font-medium text-foreground mb-4">Direct Sales Breakdown</h3>
                <div className="space-y-2 text-sm">
                  {[["VPN", reportData.direct?.VPN], ["Signal Channel", reportData.direct?.signalChannel], ["Service", reportData.direct?.service], ["Box", reportData.direct?.box], ["License", reportData.direct?.license]].map(([label, val]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-muted-foreground">{label}:</span>
                      <span className="font-mono text-foreground">{val || 0}</span>
                    </div>
                  ))}
                  <div className="border-t border-border/40 pt-2 flex justify-between font-medium">
                    <span className="text-foreground">Total:</span>
                    <span className="font-mono text-ginox-green">{reportData.direct?.total || 0}</span>
                  </div>
                </div>
              </div>
              <div className="border border-white/[0.08] rounded-lg p-5">
                <h3 className="text-sm font-medium text-foreground mb-4">Indirect Sales Breakdown</h3>
                <div className="space-y-2 text-sm">
                  {[["VPN", reportData.inDirect?.VPN], ["Signal Channel", reportData.inDirect?.signalChannel], ["Service", reportData.inDirect?.service], ["Box", reportData.inDirect?.box], ["License", reportData.inDirect?.license]].map(([label, val]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-muted-foreground">{label}:</span>
                      <span className="font-mono text-foreground">${formatCurrency(val)}</span>
                    </div>
                  ))}
                  <div className="border-t border-border/40 pt-2 flex justify-between font-medium">
                    <span className="text-foreground">Total:</span>
                    <span className="font-mono text-ginox-green">${formatCurrency(reportData.inDirect?.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-white/[0.08] pt-5">
            <h2 className="text-base font-medium text-foreground mb-4">Group Details</h2>
            <div className="space-y-4">
              {reportData.groups.map((group, index) => (
                <div key={index} className="rounded-lg border border-border/70 bg-white/[0.02] p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium text-foreground">Group: {group.group}</h3>
                    <span className="font-mono text-ginox-green font-bold">${formatCurrency(group.totalSales)}</span>
                  </div>
                  {reportType === "sales" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-md border border-border/40 bg-white/[0.02] p-3">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Direct Sales</h4>
                        <div className="space-y-1 text-sm">
                          {[["VPN", group.directSales?.vpnSales], ["Signal", group.directSales?.signalSales], ["Service", group.directSales?.serviceSales], ["Box", group.directSales?.boxSales], ["License", group.directSales?.licenseSales]].map(([label, val]) => (
                            <div key={label} className="flex justify-between">
                              <span className="text-muted-foreground">{label}:</span>
                              <span className="font-mono text-foreground">{val || 0}</span>
                            </div>
                          ))}
                          <div className="border-t border-border/40 pt-1 flex justify-between font-medium">
                            <span className="text-foreground">Total:</span>
                            <span className="font-mono text-foreground">{group.directSales?.totalSales || 0}</span>
                          </div>
                        </div>
                      </div>
                      <div className="rounded-md border border-border/40 bg-white/[0.02] p-3">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Indirect Sales</h4>
                        <div className="space-y-1 text-sm">
                          {[["VPN", group.inDirectSales?.vpnSales], ["Signal", group.inDirectSales?.signalSales], ["Service", group.inDirectSales?.serviceSales], ["Box", group.inDirectSales?.boxSales], ["License", group.inDirectSales?.licenseSales]].map(([label, val]) => (
                            <div key={label} className="flex justify-between">
                              <span className="text-muted-foreground">{label}:</span>
                              <span className="font-mono text-foreground">${formatCurrency(val)}</span>
                            </div>
                          ))}
                          <div className="border-t border-border/40 pt-1 flex justify-between font-medium">
                            <span className="text-foreground">Total:</span>
                            <span className="font-mono text-foreground">${formatCurrency(group.inDirectSales?.totalSales)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-md border border-border/40 bg-white/[0.02] p-3">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Direct Sales</h4>
                        <div className="text-2xl font-bold font-mono text-foreground">{group.directSales || 0}</div>
                      </div>
                      <div className="rounded-md border border-border/40 bg-white/[0.02] p-3">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Indirect Sales</h4>
                        <div className="text-2xl font-bold font-mono text-foreground">${formatCurrency(group.inDirectSales)}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : reportData ? (
        <div className="rounded-lg border border-border/70 bg-card p-12 text-center">
          <p className="text-muted-foreground">No {reportType === "sales" ? "sales" : "box sales"} records found for the selected user and date range.</p>
        </div>
      ) : null}
    </div>
  );
};

export default UserSalesReport;
